import { useState, useEffect, useMemo } from 'react'

import { ChevronDown, Check } from 'lucide-react'
import {
  type Control,
  type FieldPathValue,
  type FieldValues,
  type Path,
  useController,
  type UseFormSetValue,
} from 'react-hook-form'

import {
  Button,
  Input,
  InputWrapper,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/shared/components/ui'
import { FormControl, FormField, FormItem, FormLabel } from '@/shared/components/ui/form'
import { useIsMobile } from '@/shared/hooks'
import { cn } from '@/shared/lib'
import { TelefoneUtils } from '@/shared/utils/telefone.utils'

interface PhoneNumberInputFieldProps<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> {
  control?: Control<TFieldValues, TContext, TTransformedValues>
  setValue: UseFormSetValue<TFieldValues>
  nationalityName: Path<TFieldValues>
  phoneName: Path<TFieldValues>
  className?: string
  label?: string
  disabled?: boolean
}

const nationalityOptions = [
  { value: 'Brasil', label: 'Brasil (+55)', displayText: 'BRA' },
  { value: 'Internacional', label: 'Internacional', displayText: 'INT' },
]

const extractDigits = (v: string | undefined | null): string => {
  if (!v) return ''
  return v.replaceAll(/\D/g, '')
}

export function PhoneNumberInputField<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>({
  control,
  setValue,
  nationalityName,
  phoneName,
  className,
  label = 'Telefone',
  disabled,
}: Readonly<PhoneNumberInputFieldProps<TFieldValues, TContext, TTransformedValues>>) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const isMobile = useIsMobile()
  const [digits, setDigits] = useState('')

  const nationalityField = useController({ control, name: nationalityName })
  const phoneField = useController({ control, name: phoneName })

  const phoneValue = typeof phoneField.field.value === 'string' ? phoneField.field.value : ''
  const nationality = typeof nationalityField.field.value === 'string' ? nationalityField.field.value : 'Brasil'
  const limit = nationality === 'Brasil' ? 11 : 20

  const currentOption = useMemo(
    () => nationalityOptions.find((o) => o.value === nationality) || nationalityOptions[0],
    [nationality],
  )

  const handleNationalityChange = (selectedNationality: string) => {
    if (selectedNationality === nationality) return
    const phoneFieldValue =
      digits && selectedNationality === 'Brasil' ? TelefoneUtils.formatarTelefoneBrasil(digits) : (digits ?? '')
    setValue(phoneName, phoneFieldValue as FieldPathValue<TFieldValues, Path<TFieldValues>>, {
      shouldValidate: true,
      shouldDirty: true,
    })

    if (!digits) setDigits('')
    if (isMobile) setSheetOpen(false)
  }

  const hasError = phoneField.fieldState.error || nationalityField.fieldState.error
  const errorMessage = phoneField.fieldState.error?.message

  useEffect(() => {
    let raw = extractDigits(phoneValue)
    if (nationality === 'Brasil' && raw.startsWith('55')) raw = raw.slice(2)
    setDigits(raw.slice(0, limit))
  }, [phoneValue, nationality, limit])

  return (
    <div className={cn('mb-0 w-full', className)}>
      <FormLabel htmlFor={phoneName as string} aria-disabled={disabled} data-error={!!hasError}>
        {label}
      </FormLabel>
      <div className="mt-2 flex">
        <FormField
          control={control}
          name={nationalityName}
          render={({ field }) => (
            <>
              {isMobile ? (
                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                  <SheetTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={disabled}
                      className="rounded-r-none border-r-0 px-3"
                      aria-invalid={!!hasError}
                    >
                      {currentOption?.displayText}
                      <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="h-[30vh]">
                    <SheetHeader>
                      <SheetTitle>Selecione a nacionalidade</SheetTitle>
                      <SheetDescription className="sr-only" />
                    </SheetHeader>
                    <div className="py-4">
                      {nationalityOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={cn(
                            'flex w-full items-center justify-between px-4 py-3 text-left text-sm',
                            'hover:bg-accent hover:text-accent-foreground rounded-md',
                            nationality === option.value && 'font-medium',
                          )}
                          onClick={() => {
                            field.onChange(option.value)
                            handleNationalityChange(option.value)
                          }}
                        >
                          {option.label}
                          {nationality === option.value && <Check className="h-4 w-4" />}
                        </button>
                      ))}
                    </div>
                  </SheetContent>
                </Sheet>
              ) : (
                <Select
                  disabled={disabled}
                  value={field.value as string}
                  defaultValue={nationalityOptions[0]?.value}
                  onValueChange={(value) => {
                    field.onChange(value)
                    handleNationalityChange(value)
                  }}
                >
                  <SelectTrigger
                    className="rounded-r-none border-r-0 bg-neutral-100 not-placeholder-shown:bg-neutral-100 disabled:border-neutral-200 disabled:bg-neutral-100 disabled:text-neutral-500 disabled:opacity-100"
                    aria-label="Selecione a nacionalidade"
                    aria-invalid={!!hasError}
                  >
                    <SelectValue placeholder="Selecione">{currentOption?.displayText}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {nationalityOptions.map((option) => (
                      <SelectItem className="h-10" key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </>
          )}
        />
        <FormField
          control={control}
          name={phoneName}
          render={({ field, fieldState }) => (
            <FormItem className="flex-1 [&>*:nth-child(1)]:mb-1 md:[&>*:nth-child(2)]:mb-0">
              <FormControl>
                <InputWrapper
                  invalid={!!fieldState.error}
                  disabled={disabled}
                  className={cn('rounded-none rounded-r-md', isMobile ? 'w-full' : '')}
                >
                  <Input
                    {...field}
                    id={phoneName as string}
                    type="tel"
                    inputMode="tel"
                    disabled={disabled}
                    placeholder={nationality === 'Brasil' ? '(DDD) Telefone' : 'DDI (DDD) Telefone'}
                    value={TelefoneUtils.formatarTelefone(digits, nationality)}
                    onChange={(e) => {
                      const prev = digits
                      let raw = extractDigits(e.target.value)
                      if (nationality === 'Brasil' && raw.startsWith('55')) raw = raw.slice(2)
                      raw = raw.slice(0, limit)

                      const prevFormatted = TelefoneUtils.formatarTelefone(prev, nationality)
                      const shrunkByOne = e.target.value.length === prevFormatted.length - 1
                      const sameDigits = raw === prev
                      let newDigits = raw
                      if (shrunkByOne && sameDigits && prev) newDigits = prev.slice(0, -1)

                      setDigits(newDigits)
                      field.onChange(TelefoneUtils.formatarTelefone(newDigits, nationality))
                    }}
                  />
                </InputWrapper>
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="h-6 md:h-8">
        <p role="alert" data-slot="form-message" className="text-destructive text-xs">
          {errorMessage}
        </p>
      </div>
    </div>
  )
}
